  <!--
Component for the daily agenda view.
Usage: <DailyAgenda :viewContext="'Month'" :date="new Date(currentDateSelected)" :agenda="eventsList" >
@author crm4f
-->
<template>
  <div v-if ="agenda.length || viewContext == 'Day'" :class="['agendaContainer', viewContext, moveUp]">

    <ClassPopUp
      :date = "new Date(event.date)"
      :title = "event.title"
      :startTime = "event.start"
      :endTime = "event.end"
      :location = "event.location"
      :locationLink = "event.roomid ? linkToMaps(event.roomid,event.roomgrpcode) : null"
      :onlineClassLink = "handleMoodleHandoff"
      :classIf = "event.classif"
      :lecturer = "event.lecturer"
      :classType = "event.classType"
      :desc = "event.details"
      :roomBooking="event.isUserBooking"
      :room="event.room"
      :roomLink="linkToMaps(event.roomid)"
      :roomid="event.roomid"
      @close = "showTimetablePopup = false"
      v-if = "showTimetablePopup"
    />

    <table class="agenda-table">
      <tr class="weekViewDateTitle">
        <th>
          <h1>{{ formatDateTitle(date) }}</h1>
        </th>
      </tr>
      <tr v-if="agenda.length == 0 && viewContext == 'Day'"><div class="nothing-scheduled"><p>Nothing scheduled</p></div></tr>
      <tr @click="onAgendaEventClick(ev)" @keyup.enter="onAgendaEventClick(ev)" :class="props.viewContext != 'Day' ?  'agendaEvent' : 'agendaEventDayContext'" :tabindex="props.viewContext != 'Day' ? '0' : null" v-bind:key="index" v-for="(ev, index) in agenda">
        <td :class="[
          props.viewContext != 'Day' ?  'agendaTimeContainer' : 'agendaTimeContainerDayContext',
          isEventPassed(ev) ? 'time-passed' : null]
        ">
          <p class="class-times">{{ formatEventTime(ev.start, false) }}<br />-<br />{{ formatEventTime(ev.end, false) }}</p>
        </td>
        <td class="agendaEventInfoContainer">
          <div v-if="agendaEventDisplayAM(ev)" class="titlebox am">am</div>
          <div v-if="agendaEventDisplayPM(ev)" class="titlebox pm">pm</div>
          <div :class="props.viewContext != 'Day' ? 'agendaEventTextContainer' : 'agendaEventTextContainerDayContext'">
            <h2 class="event-title">{{ ev.title }}</h2>
            <br/>
            <!-- for week view -->
            <div v-if="props.viewContext != 'Day'">
              <h2>{{ locationDisplay(ev.classif, ev.room, ev.location) }}</h2>
            </div>
            <!-- for day view -->
            <div class="agendaEventInfoDayContext" v-else>
              <div style="display:grid; width: 70%;">
                <p>{{ locationDisplay(ev.classif, ev.room, ev.location) }}</p>
                <br v-if="!ev.isUserBooking"/>
                <p v-if="!ev.isUserBooking" :class="ev.lecturer == undefined ? 'not-supplied' : null">{{ ev.lecturer == undefined ? 'Lecturer data not supplied' : ev.lecturer }}</p>
              </div>
              <div style="display:flex; flex-direction:column; flex-grow:1; justify-content:end;">
                <div @click="handleMoodleHandoff()" v-if="ev.classif == 'ONLIVE' || ev.classif == 'ONANY'" class="iconContainer">
                  <font-awesome-icon icon="fa-link"></font-awesome-icon>
                  <p>link</p>
                </div>
                <div @click="handleMapsHandoff(ev.roomid, ev.roomgrpcode)" v-else-if="ev.roomid" class="iconContainer">
                  <font-awesome-icon icon="fa-location-dot"></font-awesome-icon>
                  <p>location</p>
                </div>
              </div>
            </div>
          </div>
        </td>
      </tr>
    </table>
  </div>
  <div v-else-if="agenda.length == 0 && viewContext == 'Month'" class="nothing-scheduled"><p>Nothing scheduled</p></div>
</template>

<script setup>
import { defineProps, onMounted, ref, watch, nextTick, defineEmits } from 'vue'
import { useTimetableMethods } from '@/composables/TimetableMethods'
import ClassPopUp from '@/components/timetable/ClassPopUp.vue'

const { formatEventTime, linkToMaps, eventOccursOnDay, formatDateTitle } = useTimetableMethods()

const props = defineProps({
  eventsList: Array,
  viewContext: String,
  date: Date,
  moveUp: String
})

const emit = defineEmits(['weekExists'])

watch(() => props.date, (newDate)=> {
  agenda.value = props.eventsList
    .filter((ev) => eventOccursOnDay(ev, newDate))
    .sort((ev1, ev2) => {
      return new Date(ev1.start.replace(/-/g, "/")).getTime() - new Date(ev2.start.replace(/-/g, "/")).getTime();
    });

  if(agenda.value.length) {
    emit('weekExists')
  }

})

onMounted(() => {
  agenda.value = props.eventsList.filter((ev) => eventOccursOnDay(ev, props.date)).filter((ev) => eventOccursOnDay(ev, props.date)).sort((ev1, ev2) => {return new Date(ev1.start).getTime() - new Date(ev2.start).getTime()})
})

const showTimetablePopup = ref(false)
const event = ref(null)
const agenda = ref([])

function handleMoodleHandoff() {
  window.open('https://moodle.gla.ac.uk')
}

function isEventPassed (event) {
  const startTime = new Date(event.start)
  const now = Date.now()
  return now > startTime
}

function handleMapsHandoff(roomid, roomgrpcode) {
  window.location.href = linkToMaps(roomid, roomgrpcode)
}

function onAgendaEventClick(ev) {
  event.value = ev
  showTimetablePopup.value = true
}

function agendaEventDisplayAM(event) {
  const start = new Date(event.start.replace(/-/g, "/"))
  // am is anything after this:
  const am = new Date(start).setHours(0, 0, 0, 0)
  // ...and anything before this
  const pm = new Date(start).setHours(12, 0, 0, 0)
  //If event time falls within time parameters and is the first event in the agenda (to show the am label only once)
  return ((start.getTime() > am) && (start.getTime() < pm)) && (agenda.value.indexOf(event) == 0)
}

function agendaEventDisplayPM(event) {
  const start = new Date(event.start.replace(/-/g, "/"))
  // pm is anything after this:
  const pm = new Date(start).setHours(12, 0, 0, 0)
  // ...and anything before this
  const am = new Date(start).setHours(23, 59, 59, 999)

  // is the previous element in the sorted list an AM event?
  const isFirstPMEvent = (agenda.value.indexOf(event) != 0) && (new Date(agenda.value[agenda.value.indexOf(event)-1].start.replace(/-/g, "/")).getHours() < 12)
  //Is this the first item in the agenda
  const isFirstEvent = (agenda.value.indexOf(event) == 0)

  return ((start.getTime() <= am) && (start.getTime() >= pm) && (isFirstEvent || isFirstPMEvent))
}

function locationDisplay(classif, room, location) {
  if(classif == 'ONLIVE' || classif == 'ONANY') {
    return'Online'
  } else if(room) {
    return room
  } else {
    return location
  }
}

//Watch for class info popup to be shown. Change focus to close button when shown
watch(showTimetablePopup, (newValue) => {
    if (newValue) {
      nextTick(() => {
        document.querySelector('#classPopUp .closeBtn').focus()
      });
    }
});

</script>

<style scoped>
.agenda-table {
  table-layout: fixed;
  white-space: nowrap;
  width: 100%;
}

.agendaEvent {
  display: flex;
  margin-bottom: 1px;
  cursor: pointer;
}

.agendaEventDayContext {
  display: flex;
  margin-bottom: 1px;
  cursor: pointer;
}

.agendaContainer.Month {
    margin-bottom: 40px;
}

/* Remove top border from first agenda info in week and day */
.agendaContainer.Day .weekViewDateTitle + .agendaEventDayContext .agendaEventInfoContainer,
.agendaContainer.Week .weekViewDateTitle + .agendaEvent .agendaEventInfoContainer {
    border-top:none;
}

/* Bottom border for last agenda item in month and day view */
.agendaContainer.Month .agenda-table .agendaEvent:last-child .agendaEventInfoContainer,
.agendaContainer.Day .agenda-table .agendaEventDayContext:last-child .agendaEventInfoContainer{
    border-bottom: solid 1px #ccc;
}

.agendaEventInfoContainer {
  border-top: 1px solid #ccc;
  padding: 15px;
  padding-left: 0px !important;
  flex-grow: 1;
  line-height: 1;
  min-width: 0;
  z-index: 1;
}

.iconContainer {
 color: var(--uog-dark-blue);
 display: grid;
 justify-content: center;
 width: fit-content;
 margin-left: auto;
}

.agendaEventInfoDayContext {
  display: flex;
}

.agendaTimeContainerDayContext,
.agendaTimeContainer {
  color: #fff;
  background-color: var(--uog-dark-blue);
  display: flex;
  flex-flow: column wrap;
  margin: 1px 10px 0px 0px;
  justify-content: center;
  text-align: center;
  padding: 10px 0px 10px 0px;
  min-width: 60px;
  max-width: 60px;
}

.agendaTimeContainerDayContext.time-passed,
.agendaTimeContainer.time-passed {
  background-color: var(--uog-blue-10);
}

.agendaTimeContainerDayContext {
  min-height: 125px;
}

.class-times{
  line-height: normal;
}

.agendaEventTextContainer,
.agendaEventTextContainerDayContext {
  align-content: center;
  margin-top: 3px;
}


.agendaEventTextContainerDayContext h2,
.agendaEventTextContainer h2 {
  font-size:16px;
  text-align:left;
}

.agendaEventTextContainerDayContext h2.event-title,
.agendaEventTextContainer h2.event-title {
  font-weight:bold;
  margin-left:5px;
}

.agendaEventTextContainer h2,
.agendaEventTextContainer p {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.agendaEventTextContainerDayContext h2,
.agendaEventTextContainerDayContext p {
  white-space: normal;
}


.agendaEventTextContainerDayContext h2,
.agendaEventTextContainer h2,
.agendaEventTextContainerDayContext p,
.agendaEventTextContainer p {
  margin: 0px 0px 0px 5px;
}

.agendaTimeContainer p, .agendaTimeContainerDayContext p {
  margin: 0px !important;
}

.titlebox{
  float: right;
  width: fit-content;
  position: relative;
  line-height: 1;
	padding:0 5px;
	margin:-25px 30px 0px 30px;
  color: var(--uog-dark-blue);
}

.weekViewDateTitle + .agendaEventDayContext .titlebox,
.weekViewDateTitle + .agendaEvent .titlebox {
    background:transparent;
}

.agendaEventDayContext .titlebox,
.agendaEvent .titlebox,
.agendaContainer.Month .agendaEvent .titlebox {
    background:#fff;
}

.weekViewDateTitle {
  background: #F5F5F5;
  display: flex;
  align-items: center;
  height: 40px;
  position: relative;
}

.weekViewDateTitle h1 {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size:16px;
  margin-bottom: 0px;
  margin-left: 8px;
}

/* Day view icons */
.agendaEventInfoDayContext .iconContainer svg {
  margin: 0 auto;
  margin-bottom:5px;
}

.agendaEventInfoDayContext .fa-location-dot {
    width: 20px;
    height: 26px;

}

.agendaEventInfoDayContext .fa-link {
    width: 32px;
    height: 25px;
}

.agendaEventInfoDayContext .iconContainer p {
  margin: 0px;
  font-size:12px;
  text-align:center;
}

.not-supplied {
  font-style: italic;
}


</style>


<style>
/* Global styles*/
.nothing-scheduled {
    text-align: center;
    color: #757575;
    border-top: solid 1px #CCCCCC;
    padding-top: 20px;
}

.weekViewContainer {
  margin-bottom:40px
}
</style>

